import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { DollarSignIcon, BarChartIcon, TrendingUpIcon, ArrowRightIcon, CheckIcon, XIcon, UserIcon } from 'lucide-react';
export const PricingStrategiesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-4309">
      <Navbar scrolled={scrolled} data-id="element-4310" />
      <main className="pt-24 pb-20" data-id="element-4311">
        <div className="container mx-auto px-4 md:px-6" data-id="element-4312">
          <div className="max-w-4xl mx-auto" data-id="element-4313">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-4314">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-4315">
                <DollarSignIcon className="h-64 w-64" data-id="element-4316" />
              </div>
              <div className="relative z-10" data-id="element-4317">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-4318">
                  Smart Pricing Strategies for Digital Creators
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-4319">
                  Learn how to set the right price for your templates, UI kits,
                  and digital assets using psychological pricing, A/B testing,
                  and competitive research.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-4320">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4321">
                Why Pricing Matters
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-4322">
                Setting the right price for your digital products is one of the
                most critical decisions you'll make as a creator. Price too
                high, and you might scare away potential customers. Price too
                low, and you devalue your work while leaving money on the table.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-4323">
                The good news is that digital products have unique economics
                that work in your favor - once created, they can be sold
                infinitely with minimal additional costs. This guide will help
                you navigate pricing strategies that maximize both sales volume
                and revenue.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-4324">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-4325">
                  "The right price is not about charging as much as possible -
                  it's about finding the sweet spot where customers feel they're
                  getting excellent value while you're being fairly compensated
                  for your expertise."
                </p>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-4326">
                Psychological Pricing Techniques
              </h2>
              <div className="mb-8" data-id="element-4327">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-4328">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-4329">
                    1
                  </span>
                  Charm Pricing
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-4330">
                  Ending prices with .99 or .97 (e.g., $19.99 instead of $20)
                  has been proven to increase sales. Studies show that consumers
                  tend to focus on the first digit and perceive these prices as
                  significantly lower.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4331">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-4332">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4333">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4334">
                      A UI kit priced at $49.99 often psychologically feels
                      closer to $40 than $50 to customers.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-4335">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4336">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4337">
                      For products targeting price-sensitive customers or in
                      competitive niches.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-4338">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-4339">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-4340">
                    2
                  </span>
                  Prestige Pricing
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-4341">
                  Using round numbers (e.g., $100 instead of $99.99) can signal
                  quality and premium positioning. This works well for high-end
                  digital products where you want to emphasize craftsmanship and
                  value.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4342">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-4343">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4344">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4345">
                      A comprehensive design system priced at $200 rather than
                      $199 can be perceived as more professional and
                      high-quality.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-4346">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4347">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4348">
                      For premium products where you want to signal exceptional
                      quality and comprehensive value.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-4349">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-4350">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-4351">
                    3
                  </span>
                  Anchoring
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-4352">
                  Showing a higher "original" price next to your actual price
                  creates a reference point that makes your current price seem
                  like a better deal.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4353">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-4354">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4355">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4356">
                      Displaying "$149 $99" for your website template makes $99
                      seem like an excellent value.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-4357">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4358">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4359">
                      During promotions, launches, or when you want to emphasize
                      the value proposition.
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-4360">
                Practical Pricing Framework
              </h2>
              <div className="bg-gray-50 dark:bg-[#1A3330] p-6 rounded-xl mb-8" data-id="element-4361">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-4362">
                  Step-by-Step Pricing Process
                </h3>
                <ol className="space-y-4" data-id="element-4363">
                  <li className="flex" data-id="element-4364">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-4365">
                      1
                    </span>
                    <div data-id="element-4366">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-4367">
                        Research competitors
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4368">
                        Identify 5-10 similar products and note their pricing,
                        features, and positioning.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-4369">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-4370">
                      2
                    </span>
                    <div data-id="element-4371">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-4372">
                        Calculate your costs
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4373">
                        Include time spent, software costs, and platform fees.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-4374">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-4375">
                      3
                    </span>
                    <div data-id="element-4376">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-4377">
                        Assess your unique value
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4378">
                        What makes your product better or different? This can
                        justify higher pricing.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-4379">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-4380">
                      4
                    </span>
                    <div data-id="element-4381">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-4382">
                        Consider your target audience
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4383">
                        Are they professionals who can expense purchases?
                        Hobbyists? Students?
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-4384">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-4385">
                      5
                    </span>
                    <div data-id="element-4386">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-4387">
                        Test multiple price points
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4388">
                        If possible, A/B test different prices or adjust pricing
                        over time to find the optimal point.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-4389">
                Pricing Models for Digital Products
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-4390">
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-4391">
                  <div className="flex items-center mb-4" data-id="element-4392">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3" data-id="element-4393">
                      <BarChartIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-4394" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4395">
                      Tiered Pricing
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-4396">
                    Offer multiple versions of your product at different price
                    points (e.g., Basic, Pro, Enterprise).
                  </p>
                  <div className="space-y-2" data-id="element-4397">
                    <div className="flex items-start" data-id="element-4398">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-4399" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4400">
                        Captures customers at different budget levels
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-4401">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-4402" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4403">
                        Creates natural upsell opportunities
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-4404">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-4405" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4406">
                        Can be complex to manage multiple versions
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-4407">
                  <div className="flex items-center mb-4" data-id="element-4408">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3" data-id="element-4409">
                      <TrendingUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-4410" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4411">
                      Freemium
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-4412">
                    Offer a free basic version with paid upgrades for premium
                    features.
                  </p>
                  <div className="space-y-2" data-id="element-4413">
                    <div className="flex items-start" data-id="element-4414">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-4415" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4416">
                        Lower barrier to entry for new customers
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-4417">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-4418" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4419">
                        Builds trust before asking for payment
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-4420">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-4421" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-4422">
                        Free users may never convert to paid
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-[#1A3330] pt-10 mt-10" data-id="element-4423">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4424">
                  Case Study: Successful Pricing Optimization
                </h2>
                <div className="bg-gray-50 dark:bg-[#1A3330] rounded-xl p-6 mb-8" data-id="element-4425">
                  <div className="flex items-center mb-4" data-id="element-4426">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-4427">
                      <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Alex Morgan" className="w-full h-full object-cover" data-id="element-4428" />
                    </div>
                    <div data-id="element-4429">
                      <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-4430">
                        Alex Morgan
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4431">
                        UI Kit Creator
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-4432">
                    "I initially priced my UI kit at $39, which was lower than
                    competitors because I was new to the market. After 3 months,
                    I raised the price to $59 and added more components to
                    justify the increase."
                  </p>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-4433">
                    "The surprising result: not only did sales volume stay
                    roughly the same, but my revenue increased by 51%. I later
                    introduced a basic version at $39 and an extended version at
                    $89, which further increased total revenue by offering
                    options for different customer segments."
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-4 text-center" data-id="element-4434">
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-4435">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4436">
                        Initial Price
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-4437">
                        $39
                      </p>
                    </div>
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-4438">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4439">
                        Optimal Price
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-4440">
                        $59
                      </p>
                    </div>
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-4441">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-4442">
                        Revenue Increase
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-4443">
                        +51%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#0D3C45]/5 dark:bg-[#0D3C45]/20 rounded-xl p-6 mt-12" data-id="element-4444">
                <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-4445">
                  Key Takeaways
                </h2>
                <ul className="space-y-3" data-id="element-4446">
                  <li className="flex items-start" data-id="element-4447">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-4448">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-4449" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-4450">
                      Don't undervalue your work - many creators price too low
                      initially
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-4451">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-4452">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-4453" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-4454">
                      Test different price points - the market will tell you
                      what works
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-4455">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-4456">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-4457" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-4458">
                      Consider offering multiple tiers to capture different
                      customer segments
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-4459">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-4460">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-4461" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-4462">
                      Focus on communicating value, not just features or
                      specifications
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-4463">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-4464">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4465">
                <a href="/learn/product-bundles" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-4466">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-4467">
                    Product Bundles That Sell
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-4468">
                    Learn how to create irresistible product bundles that
                    increase your average order value.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-4469">
                    <span data-id="element-4470">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-4471" />
                  </div>
                </a>
                <a href="/learn/limited-offers" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-4472">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-4473">
                    Creating Limited-Time Offers
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-4474">
                    Strategies for creating urgency and scarcity to boost sales
                    of your digital products.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-4475">
                    <span data-id="element-4476">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-4477" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-4478">
              <h2 className="text-2xl font-bold mb-4" data-id="element-4479">
                Ready to optimize your pricing?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-4480">
                Apply these strategies to your digital products and start
                maximizing your revenue today.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-4481">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4482" />
    </div>;
};